import { Injectable, input } from '@angular/core';
import {
	FormControl,
	FormGroup,
	Validators,
	FormBuilder,
	ValidatorFn,
} from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import * as _ from 'lodash';
import { FormSchemaApiService, AppConfig } from './form-schema-api.service';
import { noFutureDate, noPastDate } from './date-range.validators';
import { isJourneyCompleteUrl } from './journey-complete';
import moment from 'moment';
import { Subject, filter } from 'rxjs';
import * as he from 'he';

// Full UK postcode pattern, including the GIR 0AA special case and the
// letter-position rules. Used both by the address lookup and by the
// "postcode" schema validation rule so manual entry and lookup agree.
const UK_POSTCODE_PATTERN =
	/^([Gg][Ii][Rr] 0[Aa]{2}|((([A-Za-z][0-9]{1,2})|([A-Za-z][A-HJ-Ya-hj-y][0-9]{1,2})|([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-HJ-Ya-hj-y][0-9]?[A-Za-z]))\s?[0-9][A-Za-z]{2}))$/;

/**
 * A "*" wildcard rule asks whether the field has been answered. An unticked
 * checkbox answers `false`, which is a real value but not an answer - without
 * this an unticked box reads as present and anything shown by it can never be
 * hidden again (BRPO-60).
 */
function hasWildcardValue(value: any): boolean {
	return value !== null && value !== undefined && value !== '' && value !== false;
}

export interface CompanyHideEvent {
	companyIds: string[];
	hidden: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class FormBuilderService {
	constructor(
		private formSchemaApiService: FormSchemaApiService,
		private formBuilder: FormBuilder,
		private router: Router
	) {
		// Root singleton: this runs once per full page load, not on SPA nav.
		// Drop cached lookup validations so a reload can't restore a stale lock
		// (which would open the gate with no reference value behind it).
		sessionStorage.removeItem('form_builder_lookups');

		// The same cache also has to go once a booking completes. It is keyed to
		// the tab, not the booking, and restoreLookupStates() reasserts
		// lookupUsed: false without asking DynamoDB again - so without this a
		// second run through the journey in the same tab would sail past the
		// gate on a Home Office reference that checkout has just consumed
		// (RPD-25). Clearing above is not enough by itself: booking again from
		// the confirmation page is SPA navigation, so nothing reloads the page
		// and this constructor never runs a second time.
		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd => event instanceof NavigationEnd
				)
			)
			.subscribe(({ urlAfterRedirects }) => {
				if (isJourneyCompleteUrl(urlAfterRedirects)) {
					sessionStorage.removeItem('form_builder_lookups');
				}
			});
	}

	questions: any = {};
	forms: any = {};
	schema: any = {};
	companyId = '';
	appConfig!: AppConfig;
	counter = 0;

	addressResults: Record<string, any[]> = {};
	postcodeControls: Record<string, FormControl> = {};

	companySet$ = new Subject<string>();
	companyHide$ = new Subject<CompanyHideEvent>();
	serviceSet$ = new Subject<string>();
	resourceSet$ = new Subject<string>();
	staffSet$ = new Subject<string>();
	addDuration$ = new Subject<string>();
	updateDatastore$ = new Subject<Record<string, any>>();
	formReady$ = new Subject<Record<string, any>>();
	addressSet$ = new Subject<any>();
	awsLookupSet$ = new Subject<any>();
	foundExtraItem$ = new Subject<Record<string, any>>();
	changeExtraItem$ = new Subject<Record<string, any>>();
	extraItems: Record<string, any>[] = [];
	pendingExtraItemFetches = new Set<string>();
	formServices: Record<string, any> = {};
	processing$ = new Subject<boolean>();

	// Remember what each field added to the duration, so when the user
	// changes their answer we can take the exact same amount back off.
	private addedDurations = new Map<string, number>();

	get extraItemsList() { return this.extraItems; }

	setFormResourceId(formId: string, resourceId: string): void {
		if (!this.formServices[formId]) {
			this.formServices[formId] = {};
		}
		this.formServices[formId].resource_id = resourceId;
	}

	getSchema() {
		return this.schema;
	}

	getVenueSearchResults(company: any, query: string, appConfig: AppConfig) {
		return this.formSchemaApiService.searchVenue(company, query, appConfig);
	}

	getVenues(appConfig: AppConfig) {
		return this.formSchemaApiService.getVenues(appConfig);
	}

	async buildForm(formId: string, formValues: any[], appConfig: AppConfig, companyId: string) {
		this.companyId = companyId;
		this.appConfig = appConfig;
		// if no form id, return empty form group
		if (!formId || formId === '') {
			return new FormGroup({});
		}

		const schema: any = (await this.formSchemaApiService.getFormSchema(formId, appConfig)) as any;
		// remove haf id
		let foundKey = -1;
		for (const [idx, field] of schema.schema.fields.entries()) {
			if (field.field === 'haf_id') {
				foundKey = idx;
				break;
			}
		}
		if (foundKey > -1) {
			schema.schema.fields.splice(foundKey, 1);
		}
		const formGroup: FormGroup = new FormGroup({});
		this.schema[formId] = schema;
		// Rebuilding the form wipes the duration_added flags on every field,
		// so forget what we'd tracked for this form too.
		for (const key of Array.from(this.addedDurations.keys())) {
			if (key.startsWith(`${formId}:`)) {
				this.addedDurations.delete(key);
			}
		}
		if (schema.schema) {
			this.questions[formId] = schema.schema.fields;
			for (const field of schema.schema.fields) {
				// check visibilty
				field.is_visible = false;
				field.rules_count = Object.keys(field.show || {}).length;
				field.show_on_this_page = field.page === this.router.url.replace('/', '');
				if (field.show_on_this_page) {
					const validators = this.getValidators(field);
					const foundField = formValues.find((f) => f.field === field.field);
					let f: any = false;
					if (foundField) {
						if (field.type !== 'radio') {
							f = new FormControl(foundField.value, validators);
						} else {
							f = new FormControl(foundField.value.toString(), validators);
						}
					} else {
						if (field.hasOwnProperty('address_lookup') && field.address_lookup) {
							const postcodeFieldName = `${field.field}_postcode`;
							const postcodeField = new FormControl('', []);
							formGroup.addControl(postcodeFieldName, postcodeField);
						}
						if (field.lookup?.aws_table) {
							// Mark field as having AWS lookup capability
							field.aws_lookup = true;
						}
						f = new FormControl('', validators);
					}
					formGroup.addControl(field.field, f);
				}
				else {
					console.log('skipping field not on this page', field.field, field.page);
				}
			}
		}
		this.forms[formId] = formGroup;
		console.log('form built', { form_id: formId, extra_items: this.extraItems });
		this.formReady$.next({ form_id: formId, extra_items: this.extraItems });
		// pre-parse schema
		return formGroup;
	}

	private getStoredAnswer(formId: string, fieldName: string): any {
		const raw = sessionStorage.getItem('form_builder_answers');
		if (!raw) return null;

		try {
			const store = JSON.parse(raw);
			return store?.[formId]?.[fieldName] ?? null;
		} catch {
			return null;
		}
	}

	private persistStoredAnswer(formId: string, fieldName: string, value: any): void {
		const raw = sessionStorage.getItem('form_builder_answers');
		const store = raw ? JSON.parse(raw) : {};
		store[formId] ??= {};
		store[formId][fieldName] = value;
		sessionStorage.setItem('form_builder_answers', JSON.stringify(store));
	}

	private getLookupStore(): Record<string, Record<string, { lookupResponse: any }>> {
		const raw = sessionStorage.getItem('form_builder_lookups');
		return raw ? JSON.parse(raw) : {};
	}

	// Only persist the fields the success display renders (name + home_office).
	// The full AWS response is PII-heavy and unnecessary for restore.
	private persistLookupState(formId: string, fieldName: string, lookupResponse: any): void {
		const store = this.getLookupStore();
		store[formId] ??= {};
		const minimal: Record<string, string> = {};
		if (typeof lookupResponse?.name === 'string') minimal.name = lookupResponse.name;
		if (typeof lookupResponse?.home_office === 'string') minimal.home_office = lookupResponse.home_office;
		store[formId][fieldName] = { lookupResponse: minimal };
		sessionStorage.setItem('form_builder_lookups', JSON.stringify(store));
	}

	private removeLookupState(formId: string, fieldName: string): void {
		const store = this.getLookupStore();
		if (!store[formId]) return;
		delete store[formId][fieldName];
		if (Object.keys(store[formId]).length === 0) {
			delete store[formId];
		}
		sessionStorage.setItem('form_builder_lookups', JSON.stringify(store));
	}

	// Reapply persisted AWS lookup state to field objects after the schema rebuilds.
	// Called after buildForm/populateForm so that checkAllLookupsSuccessful and the
	// lookupValidated-driven template hiding both behave as if the lookups had just
	// been run — without re-hitting DynamoDB.
	restoreLookupStates(formId: string): void {
		const stored = this.getLookupStore()[formId];
		if (!stored) return;

		const fields = this.questions[formId];
		if (!Array.isArray(fields)) return;

		for (const fieldName of Object.keys(stored)) {
			const field = fields.find((f: any) => f.field === fieldName);
			if (!field) continue;

			const lookupResponse = stored[fieldName].lookupResponse || {};
			field.lookupResponse = lookupResponse;
			field.lookupSuccess = true;
			field.lookupError = false;
			field.lookupUsed = false;
			field.lookupNameMismatch = false;
			field.lookupLoading = false;

			const matchFields = field.lookup?.match || [];
			for (const match of matchFields) {
				// Restore each control's value too, not just the lock flag,
				// so it survives a mid-journey refresh.
				const restoredValue = lookupResponse[match.dynamodb_field];
				if (restoredValue != null) {
					this.forms[formId]?.get(match.form_field)?.setValue(restoredValue, { emitEvent: false });
				}

				if (match.form_field === fieldName) continue;
				const matchField = fields.find((f: any) => f.field === match.form_field);
				if (matchField) {
					matchField.lookupValidated = true;
				}
			}
		}
	}

	/** Schema `required_checkbox_warning`: false hides the "Important" box, a string replaces its message. */
	getRequiredCheckboxWarning(formId: string): boolean | string | undefined {
		return this.schema[formId]?.schema?.required_checkbox_warning;
	}

	/** Schema `show_required_asterisk`: opt-in " *" marker on required labels. Off unless the schema sets it true. */
	getShowRequiredAsterisk(formId: string): boolean {
		return this.schema[formId]?.schema?.show_required_asterisk === true;
	}

	getType(formId: string) {
		try {
			return this.schema[formId].schema.type;
		} catch (err) {
			return [];
		}
	}

	getFields(formId: string) {
		try {
			return this.questions[formId];
		} catch (err) {
			return [];
		}
	}

	getDetailsTitle(formId: string) {
		if (this.schema.hasOwnProperty(formId)) {
			if (this.schema[formId].schema.hasOwnProperty('form_name')) {
				return this.schema[formId].schema.form_name;
			}
		}
		return 'Test form';
	}

	getParentField(formId: string, field: any) {
		if (field.field) {
			if (field.type == 'checkbox') {
				if (field.checkbox_field) {
					const found = this.questions[formId].find(
						(f: any) => f.field == field.checkbox_field
					);
					if (found) {
						return found;
					}
				}
			}
		}
		return {};
	}

	getTriggerField(formId: string, field: any, trigger: string) {
		if (field.field) {
			if (field.type == 'checkbox') {
				const found = this.questions[formId].find(
					(f: any) => f.field == trigger
				);
				if (found) {
					return found;
				}
			} else {
				const found = this.questions[formId].find(
					(f: any) => f.field == trigger
				);
				if (found) {
					return found;
				}
			}
		}
		return {};
	}

	minYear(year: number): ValidatorFn {
		return (control: FormControl): { [key: string]: any } | null => {
			if (control.value < year) {
				return {
					minYear: {
						value: control.value,
						message: 'Year cannot be less than ' + year,
					},
				};
			}
			return null;
		};
	}

	maxYear(year: number): ValidatorFn {
		return (control: FormControl): { [key: string]: any } | null => {
			if (control.value > year) {
				return {
					maxYear: {
						value: control.value,
						message: 'Year cannot be great than ' + year,
					},
				};
			}
			return null;
		};
	}

	maxDate(noOfDays: number): ValidatorFn {
		const date = new Date();
		date.setDate(date.getDate() + noOfDays);
		date.setHours(0, 0, 0, 0);

		return (control: FormControl): { [key: string]: any } | null => {
			const controlDate = new Date(control.value);
			if (controlDate > date) {
				return {
					maxDate: {
						value: control.value,
						message: 'Date cannot be in the future',
					},
				};
			}
			return null;
		};
	}

	minDate(minDateStr: string): ValidatorFn {
		const minDate = moment(minDateStr, 'DD-MM-YYYY').startOf('day');
		return (control: FormControl): { [key: string]: any } | null => {
			if (!control.value) {
				return null;
			}

			const controlDate = moment(control.value, 'YYYY-MM-DD').startOf('day');
			if (!controlDate.isValid()) {
				return { invalidDate: { value: control.value, message: 'Invalid date format' } };
			}
			if (controlDate.isBefore(minDate)) {
				return {
					minDate: {
						value: control.value,
						message: `Date cannot be in the past`,
					},
				};
			}
			return null;
		};
	}

	private postcodeValidator(): ValidatorFn {
		return (control) => {
			const value = control.value;

			// Leave "is this answered at all" to Validators.required, so an
			// optional postcode stays optional.
			if (value === null || value === undefined || String(value).trim() === '') {
				return null;
			}

			return UK_POSTCODE_PATTERN.test(String(value).trim())
				? null
				: { postcode: { message: 'Enter a valid postcode, like S70 2TA' } };
		};
	}

	getValidators(field: Record<string, any>): ValidatorFn[] {
		const validators: ValidatorFn[] = [];

		if (field.required) {
			if (field.type === 'checkbox') {
				// Validators.required treats boolean `false` as valid (it only flags
				// null/undefined/empty values), so an unticked required checkbox
				// would leave the form valid. Use requiredTrue so unticking
				// re-invalidates the form — same pattern as the consent checkboxes
				// in customer-details-form.
				validators.push(Validators.requiredTrue);
			} else {
				validators.push(Validators.required);
			}
		}

		// RPD-20: these flags used to only set the native max/min attribute on
		// the date input, which the picker honours but the reactive form does
		// not. Without a control validator a date typed in with the keyboard
		// was never checked. Validators run on every value change, so the
		// field is revalidated however it is edited.
		if (field.hide_dates_in_future) {
			validators.push(noFutureDate());
		}
		if (field.hide_dates_in_past) {
			validators.push(noPastDate());
		}

		if (Array.isArray(field.validation)) {
			field.validation.forEach((rule: any) => {

				if (typeof rule === 'string') {
					switch (rule) {
						case 'email':
							validators.push(Validators.email);
							break;

						case 'mobile':
							validators.push(
								Validators.pattern(/^(?:0|\+44)7\d{9}$/)
							);
							break;

						case 'phone':
							validators.push(
								Validators.pattern(/^(?:0|\+44)\s?\d{10}$/)
							);
							break;

						case 'contact':
							validators.push(
								Validators.pattern(/^(?:0|\+44)\s?\d{10}$/)
							);
							break;


						// Not Validators.pattern: getErrorMessage() maps every
						// "pattern" error to the phone-number message. A named
						// error carries its own message through instead.
						case 'postcode':
							validators.push(this.postcodeValidator());
							break;

						case 'length':
							validators.push(Validators.maxLength(255));
							break;
					}
				}

				if (typeof rule === 'object' && rule !== null) {
					if (rule.minLength) {
						validators.push(Validators.minLength(rule.minLength));
					}
					if (rule.maxLength) {
						validators.push(Validators.maxLength(rule.maxLength));
					}
					if (rule.minDate) {
						validators.push(this.minDate(rule.minDate));
					}
					if (rule.maxDate) {
						validators.push(this.maxDate(rule.maxDate));
					}
				}
			});
		}

		return validators;
	}

	async shouldShowConditional(formId: string, field: any, parentForm: any): Promise<boolean> {
		this.counter++
		// use the getType function to get the field type
		const fieldType = this.getType(formId);

		if (field.page !== this.router.url.replace('/', '')) {
			// not the right page for this control/question
			return false;
		}

		let retVal = false;


		if (fieldType === 'extended') {
			retVal = this.evaluateExtendedConditional(formId, field, parentForm);
		} else {
			if (field.conditional) {
				const results = []; // see line 182
				const totalTrueRequired: any = {};
				const triggerFields = field.trigger.split('|');
				totalTrueRequired[field.field] = 0;

				let triggerTmpValue = field.trigger_value;
				if (typeof triggerTmpValue !== 'string') {
					triggerTmpValue = `${triggerTmpValue}`;
				}
				const triggerExpressions = triggerTmpValue.split('|');
				let i = 0;
				for (const trigger of triggerFields) {
					const parent = this.getTriggerField(formId, field, trigger);
					const triggerValueStr = triggerExpressions[i];

					// do we have a multi-part condition?
					const parts = triggerValueStr.split('&');
					for (const triggerValue of parts) {
						totalTrueRequired[field.field] += 1; // add the conditions to field
						if (['<', '>', '!'].indexOf(triggerValue[0]) !== -1) {
							const trigger: number = parseInt(triggerValue.substr(1));
							const parentFieldValue = parentForm.get(parent.field).value;

							const doSwitch = true;

							// In this switch statement I changed the value to be an array for multipart questions.
							// The array is summed up to check if there is any truthy values, if there are then true is returned
							// did this because if a trigger value of "<18&>15|no" where only the first trigger part is true the second runs
							// last and therefor is the returned value of false.
							if (doSwitch) {
								switch (triggerValue[0]) {
									case '<':
										if (parentFieldValue < trigger) {
											results.push(true);
										}
										break;
									case '>':
										if (parentFieldValue > trigger) {
											results.push(true);
										}
										break;
									case '!':
										if (parentFieldValue !== trigger) {
											results.push(true);
										}
										break;
									default:
										results.push(false);
								}
							}
						} else {
							const parentVal = parentForm.get(parent.field)?.value;

							if (triggerValue === '*') {
								// wildcard: passes if the field has been answered
								results.push(hasWildcardValue(parentVal));
							} else if (`${parentVal}` === `${triggerValue}`) {
								results.push(true);
							} else {
								results.push(false);
							}
						}
					}

					i++;
				}

				// store errors to replace if a form is enabled
				const tempErrorStore = parentForm.controls[field.field]?.errors;
				if (triggerTmpValue.includes('&')) {
					// its multi conditional which means we can reduce the truth flag by 1 as its an OR
					totalTrueRequired[field.field] = totalTrueRequired[field.field] - 1;
				}

				// Count the true conditions we have
				const trueCount = results.filter((value) => value === true).length;

				// If the true count matches the amount of conditions we have, it means
				// the trigger statement is true
				if (trueCount >= 1) {
					retVal = true;
				} else {
					retVal = false;
				}

				if (retVal) {
					// disable fields that are not visible
					if (parentForm.controls[field.field].status === 'DISABLED') {
						parentForm.controls[field.field].enable();
					}
				} else {
					if (parentForm.controls[field.field].status !== 'DISABLED') {
						parentForm.controls[field.field].setValue('');
						parentForm.controls[field.field].markAsPristine();
						parentForm.controls[field.field].markAsUntouched();
						parentForm.controls[field.field].disable();
					}
				}
				parentForm.controls[field.field].setErrors(tempErrorStore);
			}
			else {
				retVal = true;
			}
		}

		// now we know the visibility, handle reset if needed
		// console.log(`final visibility for field ${field.field}:`, retVal);
		const extraItems = await this.setExtraItems(formId, field, this.appConfig);
		if (retVal && field.copy_data) {
			this.handleCopyData(formId, field, parentForm);
		}
		return retVal;
	}


	private handleCompanySet(companySetValue: string, formId: string, field: any, form: FormGroup): void {
		// Store service_set value in the FormControl for later retrieval
		const control = form.get(field.field);
		if (control) {
			(control as any).companySetValue = companySetValue;
			field.company_id = companySetValue;
			this.forms[formId].company_id = companySetValue;
			// cause form update so we see it
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.companySet$.next(companySetValue);
			}
		}
	}

	private parseCompanyIds(value: string | string[] | number): string[] {
		const raw = Array.isArray(value) ? value : String(value).split(',');
		return raw.map((id) => String(id).trim()).filter((id) => id.length > 0);
	}

	private handleCompanyHide(companyHideValue: string | string[], formId: string, field: any, form: FormGroup): void {
		const control = form.get(field.field);
		if (control) {
			(control as any).companyHideValue = companyHideValue;
			(control as any).company_hidden = true;
			field.company_hidden = true;
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.companyHide$.next({ companyIds: this.parseCompanyIds(companyHideValue), hidden: true });
			}
		}
	}

	private handleCompanyUnhide(companyHideValue: string | string[], formId: string, field: any, form: FormGroup): void {
		const control = form.get(field.field);
		if (control) {
			(control as any).company_hidden = false;
			field.company_hidden = false;
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.companyHide$.next({ companyIds: this.parseCompanyIds(companyHideValue), hidden: false });
			}
		}
	}

	private handleServiceSet(serviceSetValue: string, formId: string, field: any, form: FormGroup): void {
		// Store service_set value in the FormControl for later retrieval
		const control = form.get(field.field);
		if (control) {
			(control as any).serviceSetValue = serviceSetValue;
			field.service_id = serviceSetValue;
			if (!this.formServices[formId]) {
				this.formServices[formId] = {};
			}
			this.formServices[formId].service_id = serviceSetValue;
			// cause form update so we see it
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.serviceSet$.next(serviceSetValue);
			}
		}
	}

	private handleResourceSet(resourceSetValue: string, formId: string, field: any, form: FormGroup): void {
		// Store service_set value in the FormControl for later retrieval
		const control = form.get(field.field);
		if (control) {
			(control as any).resourceSetValue = resourceSetValue;
			field.resource_id = resourceSetValue;
			this.forms[formId].resource_id = resourceSetValue;
			if (!this.formServices[formId]) {
				this.formServices[formId] = {};
			}
			this.formServices[formId].resource_id = resourceSetValue;
			// cause form update so we see it
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.resourceSet$.next(resourceSetValue);
			}
		}
	}

	private handleStaffSet(staffSetValue: string, formId: string, field: any, form: FormGroup): void {
		// Store service_set value in the FormControl for later retrieval
		const control = form.get(field.field);
		if (control) {
			(control as any).staffSetValue = staffSetValue;
			field.staff_id = staffSetValue;
			this.forms[formId].staff_id = staffSetValue;
			// cause form update so we see it
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				this.staffSet$.next(staffSetValue);
			}
		}
	}

	private handleBookerFields(field: any, form: FormGroup): void {
		const bookerKeys = ['booker_first_name', 'booker_last_name', 'booker_email', 'booker_phone'];
		for (const key of bookerKeys) {
			if (field[key] === 'true' || field[key] === true) {
				const control = form.get(field.field);
				if (control && control.value) {
					this.updateDatastore$.next({ [key]: control.value });
				}
			}
		}
	}

	private handleCopyData(formId: string, field: any, form: FormGroup): void {
		const copyData = field.copy_data;
		if (!copyData?.from || !copyData?.to) return;

		const control = form.get(field.field);
		const currentValue = control?.value;

		let shouldCopy = false;
		if (copyData.answer !== undefined && copyData.answer !== null) {
			shouldCopy = `${currentValue}` === `${copyData.answer}`;
		} else {
			shouldCopy = currentValue !== null && currentValue !== undefined && currentValue !== '';
		}
		if (!shouldCopy) return;

		// Copy each "from" field value to the corresponding "to" field
		copyData.from.forEach((fromFieldName: string, index: number) => {
			const toFieldName = copyData.to[index];
			if (!toFieldName) return;

			// Read source: form control (same page) or sessionStorage (cross-page)
			let sourceValue: any = null;
			const sourceControl = form.get(fromFieldName);
			if (sourceControl) {
				sourceValue = sourceControl.value;
			} else {
				sourceValue = this.getStoredAnswer(formId, fromFieldName);
			}
			if (sourceValue === null || sourceValue === undefined) return;

			// Write target: form control (if on page) + always sessionStorage
			const targetControl = form.get(toFieldName);
			if (targetControl) {
				targetControl.setValue(sourceValue, { emitEvent: false });
			}
			this.persistStoredAnswer(formId, toFieldName, sourceValue);
		});
	}

	private handleAddDuration(durationValue: string, formId: string, field: any, form: FormGroup): void {
		// Store service_set value in the FormControl for later retrieval
		const control = form.get(field.field);
		if (control) {
			console.log('ctrl', control);
			(control as any).add_duration = durationValue;
			(control as any).duration_added = true;
			field.duration_added = true;
			field.add_duration = durationValue;
			// cause form update so we see it
			control.updateValueAndValidity({ onlySelf: true });
			console.log('ctrl', control);
			if (field.page === this.router.url.replace('/', '')) {
				// If this field already contributed a duration, take the old amount
				// back off first so we don't double-count when the user changes answer.
				const key = `${formId}:${field.field}`;
				const alreadyAdded = this.addedDurations.get(key) ?? 0;
				const toAdd = Number(durationValue) - alreadyAdded;
				if (toAdd !== 0) {
					this.addDuration$.next(String(toAdd));
				}
				this.addedDurations.set(key, Number(durationValue));
			}
		}
	}

	private handleRemoveDuration(durationValue: string, formId: string, field: any, form: FormGroup): void {
		const control = form.get(field.field);
		if (control) {
			(control as any).duration_added = false;
			field.duration_added = false;
			control.updateValueAndValidity({ onlySelf: true });
			if (field.page === this.router.url.replace('/', '')) {
				// Take off exactly what this field added — not whatever the caller
				// passed in, since that value can drift.
				const key = `${formId}:${field.field}`;
				const toRemove = this.addedDurations.get(key) ?? Number(durationValue);
				this.addDuration$.next(String(-toRemove));
				this.addedDurations.delete(key);
			}
		}
	}

	private resolveDynamicTitle(title: string, form: FormGroup): string {
		const regex = /{([^}]+)}/g;

		return title.replace(regex, (match, fieldName) => {
			const control = form.get(fieldName);

			if (!control) {
				console.warn(`Missing control "${fieldName}" referenced in title "${title}"`);
				return '';
			}

			const value = control.value;
			return value !== undefined && value !== null ? value : '';
		});
	}

	async getAddress(formId: string, fieldName: string, appConfig: AppConfig) {
		console.log(this.questions);
		const postcodeCtrl = this.forms[formId].get(`${fieldName}_postcode`);
		const field = this.questions[formId].find((f: any) => f.field === fieldName);
		field.badPostcode = false;
		if (field && field.field) {
			console.log('postcode ctrl', postcodeCtrl.value);
			const pattern = UK_POSTCODE_PATTERN;
			if (postcodeCtrl && postcodeCtrl.value && postcodeCtrl.value.length > 0) {
				if (pattern.test(postcodeCtrl?.value)) {
					field.addressLoading = true;
					this.processing$.next(true);
					try {
						let data: any = await this.formSchemaApiService.getAddressLookup(postcodeCtrl.value, appConfig);
						
						if (data.results) {
							data = data.results;
						}
						
						const addresses = data || [];
						field.addresses = [];

						for (const item of addresses) {
							if (item.DPA) {
								field.addresses.push({ address: item.DPA });
							} else {
								field.addresses.push({ address: item.LPI });
							}
						}
						postcodeCtrl.updateValueAndValidity();
					} finally {
						field.addressLoading = false;
						this.processing$.next(false);
					}
				}
				else {
					console.error(`Invalid postcode format for field "${fieldName}": ${postcodeCtrl.value}`);
					field.badPostcode = true;
					return false;
				}
			}
			else {
				console.error(`No postcode value provided for field "${fieldName}"`);
				field.badPostcode = true;
				return false;
			}
		}
		return false;
	}

	setAddress(event: any, formId: string, field: any) {
		const ctrl = this.forms[formId].get(field.field);
		if (ctrl) {
			field.selected_address = ctrl.value;
			this.addressSet$.next(ctrl.value);
		}
	}

	async getAwsLookup(formId: string, fieldName: string, appConfig: AppConfig) {
		const field = this.questions[formId].find((f: any) => f.field === fieldName);

		if (!field || !field.lookup?.aws_table) {
			console.error(`Field "${fieldName}" is not an AWS lookup field`);
			return false;
		}

		field.lookupError = false;
		field.lookupUsed = false;
		field.lookupLoading = true;
		field.lookupResponse = null;
		field.lookupNameMismatch = false;

		const lookup = field.lookup;
		const table = lookup.aws_table;
		const matchFields = lookup.match || [];

		// Get values from form fields specified in match array
		// The first match entry with the current field is the primary reference
		// Other entries are for validation
		let primaryReference = '';
		let primaryDynamoField = '';
		let secondaryReference = '';
		let secondaryDynamoField = '';

		for (const match of matchFields) {
			const formFieldName = match.form_field;
			const dynamoField = match.dynamodb_field;

			// Try to get value from form or from stored answers
			let value = '';
			const ctrl = this.forms[formId]?.get(formFieldName);
			if (ctrl && ctrl.value) {
				value = ctrl.value;
			} else {
				value = this.getStoredAnswer(formId, formFieldName) || '';
			}

			// If this is the current field, it's the primary lookup
			if (formFieldName === fieldName) {
				primaryReference = value;
				primaryDynamoField = dynamoField;
			} else if (!secondaryReference) {
				// First non-current field becomes secondary
				secondaryReference = value;
				secondaryDynamoField = dynamoField;
			}
		}

		if (!primaryReference) {
			console.error(`No reference value provided for field "${fieldName}"`);
			field.lookupError = true;
			field.lookupLoading = false;
			return false;
		}

		try {
			const response: any = await this.formSchemaApiService.awsLookup(
				appConfig,
				table,
				primaryDynamoField,
				primaryReference,
				secondaryReference || undefined,
				secondaryDynamoField || undefined
			);

			field.lookupLoading = false;

			if (response.error) {
				console.error(`AWS lookup error for field "${fieldName}":`, response.message);
				field.lookupError = true;
				return false;
			}

			// Check response code: 1 = found, 2 = no data, 3 = error
			if (response.custom_response_code === 1) {
				// Check if reference is already used
				if (response.reference?.used === true) {
					field.lookupUsed = true;
					field.lookupError = true;
					const mainCtrl = this.forms[formId].get(fieldName);
					if (mainCtrl) {
						mainCtrl.setErrors({ used: true });
					}
					return false;
				}

				// Validate name match if secondary field was provided
				if (secondaryReference && secondaryDynamoField === 'name' && response.reference?.name) {
					const responseName = response.reference.name.toLowerCase().trim();
					const enteredName = secondaryReference.toLowerCase().trim();
					if (responseName !== enteredName) {
						field.lookupError = true;
						field.lookupNameMismatch = true;
						const mainCtrl = this.forms[formId].get(fieldName);
						if (mainCtrl) {
							mainCtrl.setErrors({ nameMismatch: true });
						}
						return false;
					}
				}

				// Success - store the response and emit
				field.lookupResponse = response.reference;
				field.lookupSuccess = true;

				this.persistLookupState(formId, fieldName, response.reference);

				// Mark match fields as part of a validated lookup (hides their inputs in the UI)
				for (const match of matchFields) {
					if (match.form_field !== fieldName) {
						const matchField = this.questions[formId]?.find((f: any) => f.field === match.form_field);
						if (matchField) {
							matchField.lookupValidated = true;
						}
					}
				}

				const mainCtrl = this.forms[formId].get(fieldName);
				if (mainCtrl) {
					mainCtrl.setErrors(null);

					// Store the reference value from the response as the form control value
					// Check if lookup config specifies which response field to use
					const storeField = field.lookup?.store_response_field;
					if (storeField && response.reference?.[storeField]) {
						mainCtrl.setValue(response.reference[storeField], { emitEvent: true });
					} else if (response.reference?.home_office) {
						// Default to home_office reference if available
						mainCtrl.setValue(response.reference.home_office, { emitEvent: true });
					}

					// Trigger form update to refresh visibility of dependent fields
					mainCtrl.updateValueAndValidity({ emitEvent: true });
				}
				this.awsLookupSet$.next({
					field: fieldName,
					reference: response.reference,
					success: true
				});
				// Emit lookupResponse to parent app's datastore
				this.updateDatastore$.next({
					type: 'lookupResponse',
					field: fieldName,
					lookupResponse: response.reference
				});
				return true;
			} else {
				// No data found or error
				field.lookupError = true;
				const mainCtrl = this.forms[formId].get(fieldName);
				if (mainCtrl) {
					mainCtrl.setErrors({ notFound: true });
				}
				return false;
			}
		} catch (error) {
			console.error(`AWS lookup failed for field "${fieldName}":`, error);
			field.lookupError = true;
			field.lookupLoading = false;
			return false;
		}
	}

	clearAwsLookup(formId: string, fieldName: string) {
		this.removeLookupState(formId, fieldName);

		const field = this.questions[formId]?.find((f: any) => f.field === fieldName);
		if (field) {
			field.lookupError = false;
			field.lookupUsed = false;
			field.lookupLoading = false;
			field.lookupResponse = null;
			field.lookupSuccess = false;
			field.lookupNameMismatch = false;

			// Clear lookupValidated on match fields and reset their form values
			const matchFields = field.lookup?.match || [];
			for (const match of matchFields) {
				if (match.form_field !== fieldName) {
					const matchField = this.questions[formId]?.find((f: any) => f.field === match.form_field);
					if (matchField) {
						matchField.lookupValidated = false;
					}
					const matchCtrl = this.forms[formId]?.get(match.form_field);
					if (matchCtrl) {
						matchCtrl.setValue('', { emitEvent: false });
					}
				}
			}
		}

		const mainCtrl = this.forms[formId]?.get(fieldName);
		if (mainCtrl) {
			mainCtrl.setValue('', { emitEvent: true });
			mainCtrl.setErrors(null);
		}

		this.awsLookupSet$.next({
			field: fieldName,
			reference: null,
			success: false
		});
	}

	/**
	 * Check if ALL visible lookup fields have been successfully validated.
	 * Every visible lookup on the page must have lookupSuccess === true.
	 * @param formId - The form ID
	 * @param excludeFieldName - Optional field to exclude (to prevent circular dependency)
	 */
	checkAllLookupsSuccessful(formId: string, excludeFieldName?: string): boolean {
		const questions = this.questions[formId];
		if (!questions || !Array.isArray(questions)) {
			return false;
		}

		const currentPage = this.router.url.replace('/', '');

		// Find ALL lookup fields on the current page
		const allLookupFields = questions.filter((f: any) =>
			f.lookup?.aws_table && f.page === currentPage
		);

		console.log(`checkAllLookupsSuccessful called (evaluating: ${excludeFieldName}):`);

		// Find VISIBLE lookup fields (excluding the evaluating field itself if it has a lookup)
		const visibleLookupFields = allLookupFields.filter((f: any) => {
			if (!(f.visible === true || f.is_visible === true)) {
				return false;
			}
			if (f.field === excludeFieldName) {
				return false;
			}
			return true;
		});

		// If no visible lookup fields exist, return false (need at least one)
		if (visibleLookupFields.length === 0) {
			console.log(`checkAllLookupsSuccessful: No visible lookup fields, returning false`);
			return false;
		}

		// Must be validated AND still hold a value — a flag without a value
		// (e.g. re-locked after a refresh) must not pass.
		const allSuccessful = visibleLookupFields.every((f: any) => {
			const value = this.forms[formId]?.get(f.field)?.value;
			const hasValue = value != null && String(value).trim() !== '';
			return f.lookupSuccess === true && hasValue;
		});

		console.log(`checkAllLookupsSuccessful: ${visibleLookupFields.length} visible lookup fields, allSuccessful=${allSuccessful}`);

		return allSuccessful;
	}

	async setExtraItems(formId: string, field: any, appConfig: AppConfig): Promise<Record<string, any>[]> {
		// Resolve extraItemId before the visibility check so that fee removal can fire
		// even when the field has just become hidden (fixes: fee persists after answer switch)
		let serviceId = "";
		let extraItemId = "";

		Object.keys(field).forEach((key) => {
			if (key === 'service-id' && field[key].length > 0) {
				serviceId = field[key];
			}
			if (key === 'extra-item') {
				extraItemId = field[key];
				console.log('%c[setExtraItems] resolved extra-item', 'color: green; font-weight: bold;', { field: field.field, extraItemId });
			}
			if (key === 'resource_ids' && this.formServices[formId]?.resource_id) {
				const resourceId = this.formServices[formId].resource_id;
				const resourceEntry = field.resource_ids[resourceId];
				if (resourceEntry?.['extra-item']) {
					extraItemId = resourceEntry['extra-item'];
					console.log('%c[setExtraItems] multiLocationRevenueCodes: resolved extra-item via resource_ids', 'color: green; font-weight: bold;', { field: field.field, resourceId, extraItemId });
				}
			}
			if (key === 'extra-item-ids') {
				Object.entries(field[key]).forEach(async ([itemKey, itemValue]) => {
					if (itemValue) {
						console.log('companyId', this.companyId);
						const found = this.extraItems.find((item: any) => item.extraItemId === itemValue);
						if (!found) {
							console.log('adding extra item', itemKey, itemValue);
							if (field.service_id) {
								// const extraItem:any = await this.formSchemaApiService.getExtraItemProperties(itemKey, this.companyId, field.service_id, appConfig);
								// this.extraItems.push({
								//   field: itemKey,
								//   serviceId: serviceId,
								//   extraItemId: itemValue,
								// });
							}
						}
					}
				});
			}
		});

		const isCurrentPage = field.page === this.router.url.replace('/', '');
		const isVisible = field.visible === true || field.is_visible === true;

		// If the field has an extra-item, is on this page, but is now hidden,
		// remove the fee if it was previously tracked. Without this, the outer
		// visibility guard would prevent the removal signal from ever firing.
		if (extraItemId && isCurrentPage && !isVisible) {
			const found = this.extraItems.find((item: any) => item.extraItemId === extraItemId);
			console.log('%c[setExtraItems] hidden-field removal path', 'color: red; font-weight: bold;', { fieldName: field.field, extraItemId, found: !!found });
			if (found) {
				const key = this.extraItems.findIndex((f: any) => f.extraItemId === extraItemId);
				if (key !== -1) {
					this.extraItems.splice(key, 1);
				}
				this.foundExtraItem$.next({ item_id: extraItemId, field: field.field, item: null });
			}
			return this.extraItems;
		}

		if (!isCurrentPage || !isVisible) {
			return this.extraItems;
		}

		if (extraItemId) {
				console.log('found extra item id', extraItemId, 'on field', field.field);
				const found = this.extraItems.find((item: any) => item.extraItemId === extraItemId);
				let ctrl;
				let value = 0;
				if (field.type === 'heading') {
					value = 1;
				}
				else {
					ctrl = this.forms[formId].get(field.field);
					value = ctrl.value;
				}
				let checkVal;
				if (isNaN(value)) {
					checkVal = this.evaluateBooleanCondition(ctrl.value);
					if (checkVal === undefined) {
						const show = Object.keys(field.show)[0];
						checkVal = field.show[show] === ctrl.value;
					}
				}
				else {
					checkVal = value > 0;
				}
				if (!found) {
					if (this.formServices[formId] && this.formServices[formId].service_id) {

						if (checkVal) {
							if (this.pendingExtraItemFetches.has(extraItemId)) {
								console.log('[setExtraItems] skipping duplicate fetch for', extraItemId);
							} else {
								this.pendingExtraItemFetches.add(extraItemId);
								this.processing$.next(true);
								try {
									// getExtraItemProperties swallows HTTP failures and hands back the error
									// message (or undefined) rather than throwing, so the shape has to be checked
									// before it is used. Writing `qty` onto a non-object threw out of this method,
									// which left the id in pendingExtraItemFetches for the rest of the session:
									// every later selection logged "skipping duplicate fetch", no request was made,
									// and the fee was never shown, never totalled and never paid for.
									const extraItem: any = await this.formSchemaApiService.getExtraItemProperties(extraItemId, this.companyId, this.formServices[formId].service_id, appConfig);
									if (!extraItem || typeof extraItem !== 'object' || !extraItem.item_id) {
										console.error('[setExtraItems] extra-item lookup failed, fee not added', { field: field.field, extraItemId, serviceId: this.formServices[formId].service_id, response: extraItem });
										return this.extraItems;
									}
									extraItem.qty = (field.type === 'heading') ? 1 : value;
									extraItem.field = field.field;
									// The label the customer sees is the schema author's own
									// `extra-item-label` when they set one. JRNI's item_name is a
									// back-office string that is easy to leave wrong on a copied
									// item — Barnsley's certificate item was still called
									// "Ceremony fee", so the fees table showed two "Ceremony fee"
									// rows. No money logic keys off this string (the base-fee
									// predicates all match on questionid/item_id), so it is safe
									// to take the schema's label as authoritative.
									if (field['extra-item-label']) {
										extraItem.item_name = field['extra-item-label'];
									}
									// keep name/price/id on the tracked row so a later qty change can reuse them
									this.extraItems.push({
										field: field['field'],
										serviceId: serviceId,
										extraItemId: extraItemId,
										qty: extraItem.qty,
										item_name: extraItem.item_name,
										item_price: extraItem.item_price,
										item_id: extraItem.item_id
									});
									console.log('Emitting event for found extra item', { item_id: extraItemId, item: extraItem });
									this.foundExtraItem$.next({ item_id: extraItemId, item: extraItem });
								} finally {
									// Always release the id and the spinner: a failed lookup must leave the
									// question retryable, not dead for the rest of the session.
									this.pendingExtraItemFetches.delete(extraItemId);
									this.processing$.next(false);
								}
							}
						}
						// !found && !checkVal is a no-op — item was never added, nothing to remove
					}
					else {
						console.error('no service id set on form, cannot get extra item');
					}
				}
				else {
					if (!checkVal) {
						const key = this.extraItems.findIndex((found: any) => found.extraItemId === extraItemId);
						if (key !== -1) {
							this.extraItems.splice(key, 1);
						}
						this.foundExtraItem$.next({ item_id: extraItemId, field: field.field, item: null });
					}
					else {
						console.log('Found item qty', found['qty'], 'new qty', value);
						if (found['qty'] !== value) {
							const key = this.extraItems.findIndex((f: any) => f.extraItemId === extraItemId);
							if (key !== -1) {
								this.extraItems.splice(key, 1);
							}
							// carry name/price/id forward - dropping them zeroed the fee on every qty change
							const tmpExtraItem = {
								field: field['field'],
								serviceId: serviceId,
								extraItemId: extraItemId,
								qty: value,
								item_name: found['item_name'],
								item_price: found['item_price'],
								item_id: found['item_id']
							};
							this.extraItems.push({ ...tmpExtraItem });
							this.changeExtraItem$.next({ item_id: extraItemId, item: tmpExtraItem });
						}
					}
				}
			}

		return this.extraItems;
	}

	// Returns today's date in YYYY-MM-DD format for date input max attribute
	readonly todayDate: string = (() => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	})();

	// extended schema rules

	/**
	 * Evaluates a field's show rules and returns whether it is visible.
	 *
	 * When visible it also applies the field's actions: company, service,
	 * resource and staff setting, duration, booker fields and copy_data, plus
	 * the appointment_type write. Pass applySideEffects false to get the
	 * visibility on its own, which is what a field on another page needs.
	 */
	evaluateExtendedConditional(formId: string, field: any, form: FormGroup, applySideEffects = true): boolean {
		const rules = field.show;

		const currentPage = this.router.url.replace('/', '');
		const storedAppointmentType = sessionStorage.getItem('appointment_type');

		field.rendered_title = this.resolveDynamicTitle(field.title, form);
		field.rules_count = rules ? Object.keys(rules).length : 0;
		field.show_on_this_page = field.page === currentPage;

		// No rules means always visible
		if (!rules || Object.keys(rules).length === 0) {

			let isVisible = true;

			if (applySideEffects) {
				if (isVisible && field.company_set && field.company_set.length > 0) {
					this.handleCompanySet(field.company_set, formId, field, form);
				}
				if (isVisible && field.company_hide && !field.company_hidden) {
					this.handleCompanyHide(field.company_hide, formId, field, form);
				}
				if (isVisible && field.service_set) {
					this.handleServiceSet(field.service_set, formId, field, form);
				}
				if (isVisible && field.resource_set && field.resource_set.length > 0) {
					this.handleResourceSet(field.resource_set, formId, field, form);
				}
				if (isVisible && field.staff_set && field.staff_set.length > 0) {
					this.handleStaffSet(field.staff_set, formId, field, form);
				}
				if (isVisible && field.add_duration && !field.duration_added) {
					this.handleAddDuration(field.add_duration, formId, field, form);
				}
				if (isVisible) {
					this.handleBookerFields(field, form);
				}
				if (isVisible && field.copy_data) {
					this.handleCopyData(formId, field, form);
				}

				if (
					isVisible &&
					field.appointment_type &&
					currentPage === 'services' &&
					!storedAppointmentType
				) {
					// set the appointment type in session storage
					sessionStorage.setItem('appointment_type', field.appointment_type);
				}
			}

			if (
				currentPage === 'booking-details' &&
				field.appointment_type &&
				storedAppointmentType &&
				field.appointment_type !== storedAppointmentType
			) {
				isVisible = false;
				console.log(`appointment type check for field ${field.field}:`, isVisible);
			}

			console.log(`no rules, field ${field.field} visible:`, isVisible);
			field.is_visible = isVisible;
			return isVisible;
		}

		// Detect rule types
		const hasCustomMultipleCondition = rules.hasOwnProperty('custom_multiple_condition');
		const hasCustomMultipleConditions = rules.hasOwnProperty('custom_multiple_conditions');
		const hasCustomOrCondition = rules.hasOwnProperty('custom_or_condition');
		const hasCustomCondition = rules.hasOwnProperty('custom');
		const hasCustomAllLookupsSuccessful = rules.hasOwnProperty('custom_all_lookups_successful');

		const ruleResults: boolean[] = [];

		// Check if all lookups are successful (exclude current field if it has a lookup)
		if (hasCustomAllLookupsSuccessful) {
			const allLookupsSuccessful = this.checkAllLookupsSuccessful(formId, field.field);
			ruleResults.push(allLookupsSuccessful);
			console.log(`custom_all_lookups_successful for field ${field.field}:`, allLookupsSuccessful);
		}

		if (hasCustomCondition) {
			ruleResults.push(
				this.evaluateCustomCondition(
					formId,
					field,
					form,
					rules.custom
				)
			);
		}

		// note singular
		if (hasCustomMultipleCondition) {
			ruleResults.push(
				this.evaluateCustomMultipleCondition(
					formId,
					field,
					form,
					rules.custom_multiple_condition
				)
			);
		}

		// note plural
		if (hasCustomMultipleConditions) {
			ruleResults.push(
				this.evaluateCustomMultipleConditions(
					formId,
					field,
					form,
					rules.custom_multiple_conditions
				)
			);
		}

		if (hasCustomOrCondition) {
			ruleResults.push(
				this.evaluateCustomOrCondition(
					formId,
					field,
					form,
					rules.custom_or_condition
				)
			);
		}

		Object.entries(rules).forEach(([ruleType, ruleValue]) => {
			if (
				ruleType === 'custom_multiple_condition' ||
				ruleType === 'custom_multiple_conditions' ||
				ruleType === 'custom_or_condition' ||
				ruleType === 'custom' ||
				ruleType === 'custom_all_lookups_successful'
			) {
				return;
			}

			ruleResults.push(
				this.evaluateSimpleRule(formId, field, form, ruleType, ruleValue)
			);
		});

		let isVisible = ruleResults.some(result => result === true);

		if (applySideEffects) {
			if (isVisible && field.company_set && field.company_set.length > 0) {
				this.handleCompanySet(field.company_set, formId, field, form);
			}
			if (isVisible && field.company_hide && !field.company_hidden) {
				this.handleCompanyHide(field.company_hide, formId, field, form);
			} else if (!isVisible && field.company_hide && field.company_hidden) {
				this.handleCompanyUnhide(field.company_hide, formId, field, form);
			}
			if (isVisible && field.service_set) {
				this.handleServiceSet(field.service_set, formId, field, form);
			}
			if (isVisible && field.resource_set && field.resource_set.length > 0) {
				this.handleResourceSet(field.resource_set, formId, field, form);
			}
			if (isVisible && field.staff_set && field.staff_set.length > 0) {
				this.handleStaffSet(field.staff_set, formId, field, form);
			}
			if (isVisible && field.add_duration && !field.duration_added) {
				this.handleAddDuration(field.add_duration, formId, field, form);
			} else if (!isVisible && field.add_duration && field.duration_added) {
				this.handleRemoveDuration(field.add_duration, formId, field, form);
			}
			if (isVisible) {
				this.handleBookerFields(field, form);
			}
			if (isVisible && field.copy_data) {
				this.handleCopyData(formId, field, form);
			}
		}

		if (
			currentPage === 'booking-details' &&
			storedAppointmentType &&
			field.appointment_type &&
			field.appointment_type !== storedAppointmentType
		) {
			isVisible = false;
			console.log(`appointment type check for field ${field.field}:`, isVisible);
		}

		if (
			applySideEffects &&
			isVisible &&
			field.appointment_type &&
			currentPage === 'services' &&
			!storedAppointmentType
		) {
			sessionStorage.setItem('appointment_type', field.appointment_type);
		}

		// console.log(`final visibility setfor field ${field.field}:`, isVisible);
		field.is_visible = isVisible;
		return isVisible;
	}

	/// Schema rules \\\

	// simple schema rule
	private evaluateSimpleRule(
		formId: string,
		field: any,
		form: FormGroup,
		targetField: string,
		expectedValue: any
	): boolean {

		let value: any = null;

		const control = form.get(targetField);

		if (control) {
			value = control.value;
		} else {
			value = this.getStoredAnswer(formId, targetField);
		}

		if (value === null || value === undefined) {
			console.warn(
				`[${formId}] Missing value for "${targetField}" required by field "${field.field}"`
			);
			return false;
		}

		// "*" wildcard = any non-empty value
		if (expectedValue === '*') {
			return hasWildcardValue(value);
		}

		// "selection" = lookup item selected (has selected_resource)
		if (expectedValue === 'selection') {
			const sourceField = this.getFields(formId)
				.find((f: any) => f.field === targetField);

			return !!sourceField?.selected_resource;
		}

		// exact match
		return value === expectedValue;

	}

	// custom numeric / date expression rule
	private evaluateCustomCondition(
		formId: string,
		field: any,
		form: FormGroup,
		ruleValue: any
	): boolean {

		if (!ruleValue || typeof ruleValue !== 'object') {
			console.warn(
				`[${formId}] Invalid custom rule for field "${field.field}"`
			);
			return false;
		}

		console.log('evaluating custom condition', ruleValue);

		// AND logic: all expressions must pass
		return Object.entries(ruleValue).every(([targetField, expression]) => {

			let value: any = null;
			const control = form.get(targetField);

			if (control) {
				value = control.value;
			} else {
				value = this.getStoredAnswer(formId, targetField);
			}

			if (value === null || value === undefined) {
				console.warn(
					`[${formId}] Missing value for "${targetField}" required by field "${field.field}"`
				);
				return false;
			}

			return this.evaluateExpression(expression as string, value);
		});
	}

	// custom_multiple_condition rule
	private evaluateCustomMultipleCondition(
		formId: string,
		field: any,
		form: FormGroup,
		ruleValue: any
	): boolean {

		if (!ruleValue || !Array.isArray(ruleValue.conditions)) {
			console.warn(
				`[${formId}] Invalid custom_multiple_condition structure for field "${field.field}"`
			);
			return false;
		}

		return ruleValue.conditions.every((condition: any) => {

			const [targetField, expectedRaw] = Object.entries(condition)[0];

			// Handle custom_all_lookups_successful as a special condition
			// Exclude current field to prevent circular dependency (field's own lookup shouldn't block its visibility)
			if (targetField === 'custom_all_lookups_successful') {
				const allLookupsSuccessful = this.checkAllLookupsSuccessful(formId, field.field);
				const expected = expectedRaw === true || expectedRaw === 'true';
				const result = expected ? allLookupsSuccessful : !allLookupsSuccessful;
				console.log(`custom_all_lookups_successful condition for field ${field.field}: expected=${expected}, actual=${allLookupsSuccessful}, result=${result}`);
				return result;
			}

			let value: any = null;

			const control = form.get(targetField);

			if (control) {
				value = control.value;
			} else {
				value = this.getStoredAnswer(formId, targetField);
			}

			if (value === null || value === undefined) {
				console.warn(
					`[${formId}] Missing value for "${targetField}" required by field "${field.field}"`
				);
				return false;
			}

			const expectedValues = typeof expectedRaw === 'string'
				? expectedRaw.split(/\s+OR\s+/i).map(v => v.trim()) // split into array
				: [expectedRaw];

			// Wildcard "*"
			if (expectedValues.includes('*')) {
				return hasWildcardValue(value);
			}

			// OR logic: ANY expected value can match
			return expectedValues.includes(value);
		});
	}

	// note singular
	private evaluateCustomOrCondition(
		formId: string,
		field: any,
		form: FormGroup,
		ruleValue: any
	): boolean {

		if (!ruleValue || !Array.isArray(ruleValue.conditions)) {
			console.warn(
				`[${formId}] Invalid custom_or_condition structure for field "${field.field}"`
			);
			return false;
		}

		// OR logic return true if ANY condition is satisfied
		return ruleValue.conditions.some((condition: any) => {
			const [targetField, expectedValues] = Object.entries(condition)[0];

			let value: any = null;

			const control = form.get(targetField);

			if (control) {
				value = control.value;
			} else {
				value = this.getStoredAnswer(formId, targetField);
			}

			if (value === null || value === undefined) {
				console.warn(
					`[${formId}] Missing value "${targetField}" required for custom_or_condition on field "${field.field}"`
				);
				return false;
			}

			if (!Array.isArray(expectedValues)) {
				console.warn(
					`[${formId}] Expected an array of values for custom_or_condition on "${targetField}"`
				);
				return false;
			}

			return expectedValues.includes(value);
		});
	}

	// custom_multiple_conditions rule (OR between groups) (note plural)
	private evaluateCustomMultipleConditions(
		formId: string,
		field: any,
		form: FormGroup,
		ruleValue: any
	): boolean {

		if (!ruleValue || !Array.isArray(ruleValue.groups)) {
			console.warn(
				`[${formId}] Invalid custom_multiple_conditions structure for field "${field.field}"`
			);
			return false;
		}

		// OR logic: return true if ANY group passes
		return ruleValue.groups.some((group: any, index: number) => {

			if (!group || !Array.isArray(group.conditions)) {
				console.warn(
					`[${formId}] Invalid conditions in custom_multiple_conditions group ${index} for field "${field.field}"`
				);
				return false;
			}

			// Reuse existing AND-based logic by shape-matching
			return this.evaluateCustomMultipleCondition(
				formId,
				field,
				form,
				{ conditions: group.conditions }
			);
		});
	}


	private evaluateBooleanCondition(val: string) {
		if (val.toLowerCase() === 'yes' || val.toLowerCase() === 'true') {
			return true;
		}
		if (val.toLowerCase() === 'no' || val.toLowerCase() === 'false') {
			return false;
		}
	}

	isValidDate(dateString: string): boolean {
		// Attempt to create a Date object from the dateString
		const date: Date = new Date(dateString);

		// Check if the created Date object is valid and its string representation matches the input
		return (
			date instanceof Date &&
			!isNaN(date.getTime()) &&
			date.toISOString().slice(0, 10) === dateString
		);
	}

	private evaluateExpression(condition: string, answer: any): boolean {

		if (!condition || typeof condition !== 'string') {
			return false;
		}

		// Split "and" conditions
		const parts = condition.split(/\s+and\s+/i);

		for (let part of parts) {
			part = part.trim();

			// Decode HTML entities (&gt;, &lt;)
			part = he.decode(part);

			const match = part.match(/^(<=|>=|<|>|=)\s*(-?\d+(\.\d+)?)/);

			if (!match) {
				console.warn(`Invalid condition part: "${part}"`);
				return false;
			}

			const operator = match[1];
			const threshold = parseFloat(match[2]);
			let numericAnswer = parseFloat(answer);

			if (this.isValidDate(answer)) {
				numericAnswer = moment(answer).diff(moment(), 'days') + 1;
			}

			if (isNaN(numericAnswer)) {
				return false;
			}

			switch (operator) {
				case '<':
					if (!(numericAnswer < threshold)) return false;
					break;
				case '<=':
					if (!(numericAnswer <= threshold)) return false;
					break;
				case '>':
					if (!(numericAnswer > threshold)) return false;
					break;
				case '>=':
					if (!(numericAnswer >= threshold)) return false;
					break;
				case '=':
					if (!(numericAnswer === threshold)) return false;
					break;
				default:
					return false;
			}
		}

		return true;
	}

}
